import { useRef, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { Link2 } from "lucide-react";
import { METHODIEK } from "@/data/methodiek";

// Interactieve MethodiekScroll — de lijn scrollt met je mee.
// Per begrip drie lagen: Voor jou · Voor de professional · Onder de motorkap.

const slug = (naam) => naam.toLowerCase().replace(/\s+/g, "-");

const LAGEN = [
  { k: "voorJou", label: "Voor jou" },
  { k: "voorProfessional", label: "Voor de professional" },
  { k: "onderMotorkap", label: "Onder de motorkap" },
];

export default function MethodiekScroll() {
  const containerRef = useRef(null);
  const location = useLocation();
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start 30%", "end 70%"],
  });
  const lineHeight = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    const el = document.getElementById(id);
    if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 250);
  }, [location.hash]);

  return (
    <section
      ref={containerRef}
      className="relative py-24 sm:py-32 bg-ll-navy text-ll-cream overflow-hidden"
      data-testid="methodiek-scroll"
    >
      <div className="max-w-[1200px] mx-auto px-6 sm:px-10 lg:px-16">
        <div className="max-w-2xl mb-20">
          <p className="text-[10px] uppercase tracking-[0.35em] text-ll-yellow">Zeven kernbegrippen</p>
          <h2 className="mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight font-light">
            De lijn loopt{" "}
            <span className="italic text-ll-coral">met je mee</span>.
          </h2>
          <p className="mt-6 text-ll-cream/80 leading-relaxed">
            Elk begrip krijgt zijn eigen kleur — die kleur betekent iets. Klik open om te lezen in de taal die past: voor jou, voor de professional, of onder de motorkap.
          </p>
        </div>

        <div className="relative pl-14 sm:pl-24">
          <div className="absolute left-3 sm:left-9 top-2 bottom-2 w-[3px] bg-ll-cream/12 rounded-full overflow-hidden">
            <motion.div style={{ height: lineHeight }} className="w-full origin-top">
              <div className="w-full h-full rounded-full bg-gradient-to-b from-[#97B79D] via-[#F0B98D] to-[#E06B3F]" />
            </motion.div>
          </div>

          <ol className="space-y-16 sm:space-y-24">
            {METHODIEK.map((m) => (
              <MethodBlok key={m.n} m={m} />
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
}

function MethodBlok({ m }) {
  const blokRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: blokRef,
    offset: ["start 80%", "start 30%"],
  });
  const dotScale = useTransform(scrollYProgress, [0, 1], [0.8, 1.4]);
  const glow = useTransform(scrollYProgress, [0, 1], [0, 1]);
  const [laag, setLaag] = useState("voorJou");

  return (
    <motion.li
      ref={blokRef}
      id={slug(m.naam)}
      initial={{ opacity: 0, y: 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-15%" }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className="relative scroll-mt-24"
      data-testid={`methodiek-blok-${slug(m.naam)}`}
    >
      <motion.div
        style={{ scale: dotScale }}
        className="absolute -left-14 sm:-left-24 top-1 w-6 h-6 sm:w-9 sm:h-9 rounded-full flex items-center justify-center"
      >
        <motion.span style={{ opacity: glow }} className="absolute inset-0 rounded-full">
          <span className="absolute inset-[-6px] rounded-full blur-md opacity-70" style={{ background: m.kleur }} />
        </motion.span>
        <span className="relative w-4 h-4 sm:w-6 sm:h-6 rounded-full border-2 border-ll-cream" style={{ background: m.kleur }} />
      </motion.div>

      <div className="pb-2">
        <div className="flex items-baseline gap-4 group">
          <span className="chapter-num text-2xl sm:text-3xl" style={{ color: m.kleur }}>{m.n}</span>
          <h3 className="font-heading text-4xl sm:text-5xl lg:text-6xl italic font-light leading-[1.02]" style={{ color: m.kleur }}>
            {m.naam}
          </h3>
          <a
            href={`#${slug(m.naam)}`}
            onClick={(e) => {
              e.preventDefault();
              const url = `${window.location.origin}${window.location.pathname}#${slug(m.naam)}`;
              navigator.clipboard?.writeText(url);
              window.history.replaceState(null, "", `#${slug(m.naam)}`);
            }}
            className="opacity-0 group-hover:opacity-80 transition-opacity text-ll-cream/60 hover:text-ll-cream"
            aria-label={`Kopieer link naar ${m.naam}`}
            data-testid={`anchor-${slug(m.naam)}`}
          >
            <Link2 size={16} />
          </a>
        </div>
        <p className="mt-4 text-ll-cream/85 text-lg sm:text-xl italic leading-snug">{m.kernvraag}</p>
      </div>

      {/* 3 lagen tabs */}
      <div className="mt-8 pt-6 border-t border-ll-cream/15">
        <div className="flex flex-wrap gap-2">
          {LAGEN.map((l) => {
            const active = laag === l.k;
            return (
              <button
                key={l.k}
                type="button"
                onClick={() => setLaag(l.k)}
                data-testid={`laag-${slug(m.naam)}-${l.k}`}
                className={`text-[11px] uppercase tracking-[0.22em] rounded-full px-3.5 py-1.5 transition-all ${
                  active
                    ? "text-ll-ink"
                    : "text-ll-cream/70 hover:text-ll-cream border border-ll-cream/25"
                }`}
                style={active ? { background: m.kleur } : undefined}
              >
                {l.label}
              </button>
            );
          })}
        </div>
        <AnimatePresence mode="wait">
          <motion.p
            key={laag}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.35 }}
            className="mt-5 text-ll-cream/90 leading-relaxed max-w-3xl"
          >
            {m[laag]}
          </motion.p>
        </AnimatePresence>
      </div>
    </motion.li>
  );
}
